import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
  TextField, Snackbar, Alert, Slider, Switch, Chip, Divider, FormControlLabel
} from '@mui/material';
import axiosInstance from './axiosConfig';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Settings, Thermostat, Opacity, Lightbulb, Schedule, Refresh } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

const apiUrl = process.env.REACT_APP_API_URL;

const RoomControl = () => {
  const { translate } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [roomConfig, setRoomConfig] = useState({});
  const [relays, setRelays] = useState([]);
  const [lightSchedule, setLightSchedule] = useState({ start: '', end: '' });
  const [temperatureThreshold, setTemperatureThreshold] = useState({ min: 0, max: 30 });
  const [humidityThreshold, setHumidityThreshold] = useState({ min: 40, max: 70 });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [cultivationStartDate, setCultivationStartDate] = useState('');
  const [downtime, setDowntime] = useState(0);
  const [plantVarieties, setPlantVarieties] = useState([{ variety: '', count: 0 }]);
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchRooms(selectedProject);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedRoom) {
      fetchRoomConfig(selectedRoom);
    }
  }, [selectedRoom]);

  const handleRestartCycle = async () => {
    try {
      const response = await axiosInstance.put(`/api/rooms/${selectedRoom}/restart-cycle`);
      setDowntime(response.data.downtime);
      showSnackbar(translate('roomControl.cultivationRestarted'), 'success');
      fetchRoomConfig(selectedRoom);
    } catch (error) {
      showSnackbar(translate('roomControl.cultivationRestartError'), 'error');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get(`/api/projects`);
      setProjects(response.data);
      
      const savedProjectId = localStorage.getItem('projectId');
      if (savedProjectId && response.data.find(p => p.id === savedProjectId)) {
        setSelectedProject(savedProjectId);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchRooms = async (projectId) => {
    try {
      const response = await axiosInstance.get(`/api/rooms/${projectId}`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleRestartCultivation = async () => {
    try {
      const response = await axiosInstance.put(`/api/rooms/${selectedRoom}/restart`);
      setDowntime(response.data.downtime);
      fetchRoomConfig(selectedRoom);
      showSnackbar(translate('roomControl.cultivationRestarted'), 'success');
    } catch (error) {
      showSnackbar(translate('roomControl.cultivationRestartError'), 'error');
    }
  };
  
  const fetchRoomConfig = async (roomId) => {
    try {
      const response = await axiosInstance.get(`/api/rooms/${roomId}/config`);
      const config = response.data.roomConfig;
      setRoomConfig(config);
      setRelays(response.data.relayConfigs);
      setPlantVarieties(config.plant_varieties || []);
      setLightSchedule({
        start: config.light_schedule_start || '',
        end: config.light_schedule_end || '',
      });
      setCultivationStartDate(config.cultivation_start_date || '');
  
      setTemperatureThreshold({
        min: config.min_temperature || 0,
        max: config.max_temperature || 30,
      });
      setHumidityThreshold({
        min: config.min_humidity || 40,
        max: config.max_humidity || 70,
      });
    } catch (error) {
      console.error('Error fetching room configuration:', error);
    }
  };
  
  const handleProjectChange = (event) => {
    const projectId = event.target.value;
    setSelectedProject(projectId);
    setSelectedRoom('');
    setRooms([]);
    localStorage.setItem('projectId', projectId);
  };

  const handleRoomChange = (event) => {
    const roomId = event.target.value;
    setSelectedRoom(roomId);
    fetchRoomConfig(roomId);
  };

  const handleSaveRoomConfig = async () => {
    try {
      await axiosInstance.put(`/api/rooms/${selectedRoom}/config`, {
        light_schedule_start: lightSchedule.start,
        light_schedule_end: lightSchedule.end,
        cultivation_start_date: cultivationStartDate,
        min_temperature: temperatureThreshold.min,
        max_temperature: temperatureThreshold.max,
        min_humidity: humidityThreshold.min,
        max_humidity: humidityThreshold.max,
        plant_varieties: plantVarieties
      });
      showSnackbar(translate('roomControl.roomConfigSaved'), 'success');
    } catch (error) {
      showSnackbar(translate('roomControl.roomConfigError'), 'error');
    }
  };
  
  const handleSaveRelay = async (relayId, threshold_value, action) => {
    try {
      await axiosInstance.put(`/api/relay/${relayId}`, { threshold_value, action });
      showSnackbar(translate('roomControl.relayConfigSaved'), 'success');
    } catch (error) {
      showSnackbar(translate('roomControl.relayConfigError'), 'error');
    }
  };

  const handleAddVariety = () => {
    setPlantVarieties([...plantVarieties, { variety: '', count: 0 }]);
  };
  
  const handleRemoveVariety = (index) => {
    setPlantVarieties(plantVarieties.filter((_, i) => i !== index));
  };
  
  const handleVarietyChange = (index, field, value) => {
    const newVarieties = [...plantVarieties];
    newVarieties[index][field] = value;
    setPlantVarieties(newVarieties);
  };
  
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleThemeChange = () => {
    setDarkMode(!darkMode);
  };

  return (
    <ModernLayout title={translate('roomControl.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('roomControl.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('roomControl.subtitle')}
        </Typography>
      </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Proyectos"
              value={projects.length.toString()}
              subtitle="Proyectos disponibles"
              icon={<Settings />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Rooms"
              value={rooms.length.toString()}
              subtitle="Espacios configurados"
              icon={<Settings />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Relays Activos"
              value={relays.length.toString()}
              subtitle="Dispositivos controlados"
              icon={<Settings />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Variedades"
              value={plantVarieties.length.toString()}
              subtitle="Tipos de plantas"
              icon={<Settings />}
              color="warning"
            />
          </Grid>
        </Grid>

        {/* Project and Room Selection */}
        <ModernCard
          title={translate('roomControl.projectAndRoomSelection')}
          subtitle={translate('roomControl.selectProjectAndRoom')}
          sx={{ mb: 3 }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{translate('roomControl.selectProject')}</InputLabel>
                <Select
                  value={selectedProject}
                  onChange={handleProjectChange}
                  label={translate('roomControl.selectProject')}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{translate('roomControl.selectRoom')}</InputLabel>
                <Select
                  value={selectedRoom}
                  onChange={handleRoomChange}
                  label={translate('roomControl.selectRoom')}
                  disabled={!selectedProject}
                >
                  {rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </ModernCard>

        {/* Room Configuration */}
        {selectedRoom && (
          <>
            {/* Basic Configuration */}
            <ModernCard
              title={translate('roomControl.basicConfiguration')}
              subtitle={translate('roomControl.cultivationParameters')}
              sx={{ mb: 3 }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={translate('roomControl.cultivationDuration')}
                    type="number"
                    value={roomConfig.cultivation_duration || ''}
                    onChange={(e) => setRoomConfig({ ...roomConfig, cultivation_duration: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={translate('roomControl.cultivationStartDate')}
                    type="date"
                    value={roomConfig.cultivation_start_date || ''}
                    onChange={(e) => setRoomConfig({ ...roomConfig, cultivation_start_date: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </ModernCard>

            {/* Light Schedule */}
            <ModernCard
              title={translate('roomControl.lightingSchedule')}
              subtitle={translate('roomControl.lightScheduleDescription')}
              sx={{ mb: 3 }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={translate('roomControl.lightStartTime')}
                    type="time"
                    value={roomConfig.light_schedule_start || ''}
                    onChange={(e) => setRoomConfig({ ...roomConfig, light_schedule_start: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={translate('roomControl.lightEndTime')}
                    type="time"
                    value={roomConfig.light_schedule_end || ''}
                    onChange={(e) => setRoomConfig({ ...roomConfig, light_schedule_end: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </ModernCard>

            {/* Temperature and Humidity Thresholds */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <ModernCard
                  title={translate('roomControl.temperatureRange')}
                  subtitle={translate('roomControl.optimalTemperatureRange')}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Rango: {temperatureThreshold.min}°C - {temperatureThreshold.max}°C
                    </Typography>
                    <Slider
                      value={[temperatureThreshold.min, temperatureThreshold.max]}
                      onChange={(e, newValue) => setTemperatureThreshold({ min: newValue[0], max: newValue[1] })}
                      min={0}
                      max={50}
                      valueLabelDisplay="auto"
                      sx={{ mb: 2 }}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={translate('roomControl.minTemperature')}
                          type="number"
                          value={temperatureThreshold.min}
                          onChange={(e) => setTemperatureThreshold({ ...temperatureThreshold, min: parseFloat(e.target.value) })}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={translate('roomControl.maxTemperature')}
                          type="number"
                          value={temperatureThreshold.max}
                          onChange={(e) => setTemperatureThreshold({ ...temperatureThreshold, max: parseFloat(e.target.value) })}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </ModernCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <ModernCard
                  title={translate('roomControl.humidityRange')}
                  subtitle={translate('roomControl.optimalHumidityRange')}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Rango: {humidityThreshold.min}% - {humidityThreshold.max}%
                    </Typography>
                    <Slider
                      value={[humidityThreshold.min, humidityThreshold.max]}
                      onChange={(e, newValue) => setHumidityThreshold({ min: newValue[0], max: newValue[1] })}
                      min={0}
                      max={100}
                      valueLabelDisplay="auto"
                      sx={{ mb: 2 }}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={translate('roomControl.minHumidity')}
                          type="number"
                          value={humidityThreshold.min}
                          onChange={(e) => setHumidityThreshold({ ...humidityThreshold, min: parseFloat(e.target.value) })}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={translate('roomControl.maxHumidity')}
                          type="number"
                          value={humidityThreshold.max}
                          onChange={(e) => setHumidityThreshold({ ...humidityThreshold, max: parseFloat(e.target.value) })}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </ModernCard>
              </Grid>
            </Grid>

            {/* Plant Varieties */}
            <ModernCard
              title={translate('roomControl.plantVarieties')}
              subtitle={translate('roomControl.cultivationVarieties')}
              sx={{ mb: 3 }}
            >
              <Box sx={{ p: 2 }}>
                {plantVarieties.map((variety, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                      label={translate('roomControl.variety')}
                      value={variety.variety}
                      onChange={(e) => handleVarietyChange(index, 'variety', e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        },
                      }}
                    />
                    <TextField
                      label={translate('roomControl.count')}
                      type="number"
                      value={variety.count}
                      onChange={(e) => handleVarietyChange(index, 'count', parseInt(e.target.value))}
                      sx={{
                        width: 120,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                        },
                      }}
                    />
                    <GlassButton
                      onClick={() => handleRemoveVariety(index)}
                      color="error"
                    >
                      <RemoveCircleIcon />
                    </GlassButton>
                  </Box>
                ))}
                <GradientButton
                  startIcon={<AddCircleIcon />}
                  onClick={handleAddVariety}
                >
                  {translate('roomControl.addVariety')}
                </GradientButton>
              </Box>
            </ModernCard>

            {/* Relay Configuration */}
            {relays.length > 0 && (
              <ModernCard
                title={translate('roomControl.relayConfiguration')}
                subtitle={translate('roomControl.automaticDeviceControl')}
                sx={{ mb: 3 }}
              >
                <Grid container spacing={3}>
                  {relays.map((relay) => (
                    <Grid item xs={12} md={6} key={relay.id}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                          {relay.name}
                        </Typography>
                        <TextField
                          fullWidth
                          label={translate('roomControl.relayThreshold')}
                          type="number"
                          value={relay.threshold_value || ''}
                          onChange={(e) => handleSaveRelay(relay.id, parseFloat(e.target.value), relay.action)}
                          sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                            },
                          }}
                        />
                        <Chip
                          label={relay.action}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </ModernCard>
            )}

            {/* Save Configuration */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <GradientButton
                onClick={handleSaveRoomConfig}
                size="large"
              >
                {translate('roomControl.saveRoomConfig')}
              </GradientButton>
            </Box>
          </>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
    </ModernLayout>
  );
};

export default RoomControl;
